import { c as _c } from "react/compiler-runtime";
import type { MapData, MapDataLocationFeature } from '@repo/types/maps/map-types';
import { useEffect, useRef, useState } from 'react';
import { useNearbyPins } from './useNearbyPins';

/**
 * Captures the nearby pins list (including the initially selected pin) when a pin is first
 * selected, and keeps that list static until the selection is fully cleared.
 * This prevents the list from shifting as the user cycles through nearby pins.
 */
export function useStaticNearbyPins(selectedLocation, mapData) {
  const $ = _c(6);
  const livePins = useNearbyPins(selectedLocation, mapData);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [staticPins, setStaticPins] = useState(t0);
  const initialPinIdRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const staticPinsRef = useRef(t1);
  let t2;
  let t3;
  if ($[2] !== livePins || $[3] !== selectedLocation) {
    t2 = () => {
      if (!selectedLocation) {
        staticPinsRef.current = [];
        setStaticPins([]);
        initialPinIdRef.current = null;
        return;
      }
      if (initialPinIdRef.current !== null && !staticPinsRef.current.some(p => p.properties.externalId === selectedLocation.properties.externalId)) {
        initialPinIdRef.current = null;
        staticPinsRef.current = [];
        setStaticPins([]);
      }
      if (initialPinIdRef.current === null && livePins.length > 0) {
        initialPinIdRef.current = selectedLocation.properties.externalId;
        const pinsWithOriginal = [selectedLocation, ...livePins.filter(p_0 => p_0.properties.externalId !== selectedLocation.properties.externalId)];
        staticPinsRef.current = pinsWithOriginal;
        setStaticPins(pinsWithOriginal);
      }
    };
    t3 = [selectedLocation, livePins];
    $[2] = livePins;
    $[3] = selectedLocation;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return staticPins;
}